import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Badge from '../../../../../../components/global/Badge';
import Button from '../../../../../../components/global/Button';
import ContributorProfileBadge from '../../../../../../components/global/ContributorProfileBadge';
import AlbumFolderGalleryIcon from '../../../../../../components/icons/AlbumFolderGalleryIcon';
import MapMarkerIcon from '../../../../../../components/icons/MapMarkerIcon';
import AlbumsIcon from '../../../../../../components/icons/AlbumsIcon';
import CalendarIcon from '../../../../../../components/icons/CalendarIcon';
import LockIcon from '../../../../../../components/icons/LockIcon';
import { getAsset } from '../../../../../../util/assets';
import { getThumbBySize } from '../../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../../constants/album-folder-tags';
import { getTagsByType } from '../../../../../../util/album-folder-tags';
import { truncateText } from '../../../../../../util/text';
import { ALBUM_TYPE } from '../../../../../../constants/albums';
import VerifiedIcon from '../../../../../../components/icons/VerifiedIcon';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import getConfig from '../../../../../../util/config';
import { BASENAME } from '../../../../../../constants/basenames';

const propTypes = {
  albumFolder: PropTypes.shape({
    account: PropTypes.object,
    children: PropTypes.object,
    id: PropTypes.string,
    cover_photos: PropTypes.object,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    name: PropTypes.string,
    location: PropTypes.string,
    description: PropTypes.string,
    has_password: PropTypes.bool,
  }).isRequired,
  nameSearchField: PropTypes.string,
  authenticated: PropTypes.bool,
  // Override default navigation if this is active.
  onClick: PropTypes.func,
};

const AlbumFolderSearchResult = ({
  albumFolder,
  nameSearchField,
  authenticated,
  onClick,
}) => {
  const DATE_FORMAT = 'MMM D, Y';
  const UNIFORM_TAG_LIMIT = 3;

  const { id, account } = albumFolder;
  const creatorAvatarPhotoSrc = account?.avatar_photo
    ? getThumbBySize(account.avatar_photo.thumbnail_urls, IMAGE_SIZE_CODE.SMALL)
        .url
    : undefined;
  const { username } = account;
  const viewLinkTarget = authenticated
    ? `/app/${BASENAME.SHUTTERBUG}/hierarchy-items/${id}`
    : `/galleries/view/folder/${id}`;
  // later, for albums it will be /galleries/view/album/{id}/'

  // did the search query match a contained album name?
  const matchingAlbum =
    nameSearchField !== '' &&
    albumFolder.children.albums.records.find(album =>
      album.name.match(new RegExp(nameSearchField, 'i')),
    );

  const matchingAlbumName = matchingAlbum?.name;

  // cover photo for gallery, or placeholder graphic.
  let coverPhoto = albumFolder.cover_photos.records?.[0]
    ? getThumbBySize(
        albumFolder.cover_photos.records[0].thumbnail_urls,
        IMAGE_SIZE_CODE.LARGE,
      ).url
    : getAsset('/web-galleries/images/tiles.svg');

  // Optionally get the cover photo from the specific album that matched the query
  coverPhoto = matchingAlbum?.cover_photo
    ? getThumbBySize(
        matchingAlbum.cover_photo.thumbnail_urls,
        IMAGE_SIZE_CODE.LARGE,
      ).url
    : coverPhoto;

  // Only unique uniform tags.
  const uniformTags = [
    ...new Set(
      getTagsByType(
        ALBUM_FOLDER_TAG_TYPE.UNIFORM,
        albumFolder.tags?.records,
      ).map(tag => tag.name.toUpperCase()),
    ),
  ];
  const displayingUniformTags = uniformTags.slice(0, UNIFORM_TAG_LIMIT);

  const moreUniformTags = uniformTags.length > UNIFORM_TAG_LIMIT && (
    <Badge
      badgeStyle="primary"
      className="album-folder-tag mr-1"
      badgeSize="xs"
      key="more-uniform-tags"
    >
      + {uniformTags.length - UNIFORM_TAG_LIMIT} more&hellip;
    </Badge>
  );

  const dateRangeString = (() => {
    if (albumFolder.start_date && albumFolder.end_date) {
      return `${moment(albumFolder.start_date).format(DATE_FORMAT)} - ${moment(
        albumFolder.end_date,
      ).format(DATE_FORMAT)}`;
    }
    return albumFolder.start_date
      ? moment(albumFolder.start_date).format(DATE_FORMAT)
      : undefined;
  })();

  const hasUploadPermissions =
    !!albumFolder.auto_contributor_settings?.upload_access &&
    albumFolder.auto_contributor_settings?.upload_access !==
      CONTRIBUTOR_UPLOAD_ACCESS.NONE;
  const uploadRouteUrl = `${getConfig().app.baseUrl}/contributors/u/${
    albumFolder?.id
  }`;

  return (
    <div className="wg-search-result wg-search-result-album-folder shadowed-search-result">
      {albumFolder.album_type === ALBUM_TYPE.PRO && (
        <Badge badgeSize="sm" className="wg-search-result-badge-verified">
          Verified <VerifiedIcon />
        </Badge>
      )}
      <a
        className="cover-photo-head"
        href={viewLinkTarget}
        target="_parent"
        style={{ backgroundImage: `url("${coverPhoto}")` }}
        onClick={event => {
          if (onClick) {
            event.preventDefault();
            onClick(event);
          }
        }}
      >
        <div className="result-badges">
          {matchingAlbumName && (
            <Badge
              badgeStyle="yellow"
              className="inset-album-info-badge"
              badgeSize="sm"
            >
              <AlbumsIcon size={13} color="#000" />
              <span>{matchingAlbumName}</span>
            </Badge>
          )}
        </div>
      </a>

      <div className="result-details">
        <h4 className="result-title mb-1">
          <a
            href={viewLinkTarget}
            target="_parent"
            onClick={event => {
              if (onClick) {
                event.preventDefault();
                onClick(event);
              }
            }}
          >
            <AlbumFolderGalleryIcon size={12} color="#1A2E34" />{' '}
            {albumFolder.name}
          </a>
        </h4>

        {albumFolder.album_type === ALBUM_TYPE.SHUTTERBUG && (
          <ContributorProfileBadge
            className="mb-1"
            displayName={account?.name}
            displayUsername={username}
            displayPhotoSrc={creatorAvatarPhotoSrc}
            href={username ? `/@${username}` : undefined}
            target="_parent"
            prependText="by"
            characterWidthLimit={30}
          />
        )}

        {albumFolder.description && (
          <div className="result-description mb-0">
            {truncateText(albumFolder.description, 20)}
          </div>
        )}

        {uniformTags.length > 0 && (
          <div>
            {displayingUniformTags.map(tag => (
              <Badge
                badgeStyle="primary"
                className="album-folder-tag"
                badgeSize="xs"
                key={tag}
              >
                {tag}
              </Badge>
            ))}
            {moreUniformTags}
          </div>
        )}

        <div className="result-meta mt-1">
          {albumFolder.location && (
            <span className="result-meta-item result-meta-item-location">
              <MapMarkerIcon color="#9BA8AC" /> {albumFolder.location.name}
            </span>
          )}

          {dateRangeString && (
            <span className="result-meta-item result-meta-item-date">
              <CalendarIcon size={13} color="#9BA8AC" /> {dateRangeString}
            </span>
          )}
        </div>

        <div className="wg-search-result-footer">
          <Button
            btnStyle="yellow"
            btnSize="sm"
            className="view-result-button"
            href={viewLinkTarget}
            target="_parent"
            onClick={event => {
              if (onClick) {
                event.preventDefault();
                onClick(event);
              }
            }}
          >
            {albumFolder.has_password && (
              <LockIcon size={10} color="#000" className="mr-1" />
            )}
            Browse
          </Button>

          {hasUploadPermissions && (
            <Button
              btnSize="sm"
              className="view-result-button"
              href={uploadRouteUrl}
              target="_parent"
            >
              Upload Photos
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

AlbumFolderSearchResult.propTypes = propTypes;

export default AlbumFolderSearchResult;
